/* eslint-disable camelcase */
import { Formik, Form as FormikForm } from 'formik';
import { castArray } from 'lodash';
import PropTypes from 'prop-types';
import { useState, useCallback, useMemo, useRef } from 'react';
import { flushSync } from 'react-dom';
import { useNavigate } from 'react-router-dom';

import { Box } from '@hivebrite/design-system/Box';
import { Card } from '@hivebrite/design-system/Card';
import { toast } from '@hivebrite/design-system/Toast';

import { useTopicContext } from 'hb-react/backoffice/contexts/TopicContext';
import { useDonationContext } from 'hb-react/frontoffice/contexts/DonationContext';
import { useDefaultAppContexts } from 'hb-react/frontoffice/contexts/withDefaultAppContexts';
import AmountsAndFundsSections from 'hb-react/shared/apps/Donations/components/AmountsAndFundsSections';
import { getInitialValues } from 'hb-react/shared/apps/Donations/form/initialValues';
import {
  dynamicDonationResourcePath,
  donationBasePath,
} from 'hb-react/shared/apps/Donations/utils/whiteLabel';
import ErrorFocus from 'hb-react/shared/components/formik/components/ErrorFocus';
import ErrorsReminder from 'hb-react/shared/components/formik/components/ErrorsReminder';
import { Hidden } from 'hb-react/shared/components/formik/components/inputs';
import useFeatureFlag from 'hb-react/shared/hooks/useFeatureFlag';
import { axiosInstance as axios } from 'hb-react/shared/utils/axios';
import { goToPaymentPage } from 'hb-react/shared/utils/paymentPage';
import { FormikRecaptcha } from 'hb-react/shared/utils/recaptcha';
import { scrollTop } from 'helpers/scroll';

import CommitPayLaterButton from './CommitPayLaterButton';
import Payment from './Payment';
import SubmitButton from './SubmitButton';
import YourInformationSection from '../YourInformationSection';

const Form = ({
  order,
  initialAmount,
  initialFundId,
  initialMonthlyRecurring,
  externalId,
  promotionCode,
  submitDisabled = false,
  payLaterEnabled = false,
  payNowEnabled = true,
}) => {
  const { flagEvPaymentPageDonations } = useFeatureFlag();
  const { topicId } = useTopicContext();

  const [isPaying, setIsPaying] = useState(false);
  const [isPaymentDone, setIsPaymentDone] = useState(false);
  const [canResubmit, setCanResubmit] = useState(false);
  const [isLoadingPaymentPage, setIsLoadingPaymentPage] = useState(false);

  const {
    campaign,
    meta: {
      periodicities,
      countries,
      payment_gateway,
      advanced_options_enabled,
    },
  } = order;

  const { currentUser, currentNetwork } = useDefaultAppContexts();
  const { whitelabelURL } = useDonationContext();
  const whitelabelURLEnabled = useMemo(
    () => (currentUser ? false : whitelabelURL),
    [currentUser, whitelabelURL],
  );

  const route = useMemo(
    () =>
      dynamicDonationResourcePath(Routes.donations_path(), {
        whitelabelURL: whitelabelURLEnabled,
      }),
    [whitelabelURLEnabled],
  );

  const initialValues = useMemo(
    () => ({
      ...getInitialValues({
        order,
        campaign,
        paymentGateway: payment_gateway,
        initialAmount,
        initialFundId,
        initialMonthlyRecurring,
        externalId,
        promotionCode,
      }),
      pay_later: false,
      recaptcha_response: '',
    }),
    [
      order,
      campaign,
      payment_gateway,
      initialAmount,
      initialFundId,
      initialMonthlyRecurring,
      externalId,
      promotionCode,
    ],
  );

  const formikRef = useRef();
  const navigate = useNavigate();

  const handleError = useCallback((error) => {
    setIsPaying(false);
    setIsLoadingPaymentPage(false);
    setIsPaymentDone(false);
    setCanResubmit(true);
    formikRef.current.setFieldValue('pay_later', false);
    toast.error(
      castArray(
        error?.response?.data?.errors ||
          error?.responseText ||
          error?.errors ||
          __('Sorry, an error occurred'),
      ).join(', ') || __('Sorry, an error occurred'),
    );
  }, []);

  const handleSuccessPayment = useCallback(
    (data) => {
      scrollTop({ smooth: true });
      toast.success(__('Your payment was successful - Thank you'));
      if (data.redirect_url) {
        const redirectPath = dynamicDonationResourcePath(data.redirect_url, {
          whitelabelURL: whitelabelURLEnabled,
        });

        navigate(redirectPath);
      }
    },
    [navigate, whitelabelURLEnabled],
  );

  const submit = useCallback(() => {
    axios
      .post(donationBasePath(route), formikRef.current.values)
      .then(({ data }) => {
        handleSuccessPayment(data);
      })
      .catch(handleError);
  }, [route, handleSuccessPayment, handleError]);

  const submitBillingInfo = useCallback(
    (values) => {
      const submitPath = dynamicDonationResourcePath(
        Routes.create_payment_cart_donation_path(campaign.id),
        {
          whitelabelURL: whitelabelURLEnabled,
        },
      );

      axios
        .post(submitPath, values)
        .then(({ data: { cart_id: cartId } }) => {
          setIsLoadingPaymentPage(true);
          goToPaymentPage({ cartId, topicId });
        })
        .catch(handleError);
    },
    [campaign.id, topicId, whitelabelURLEnabled, handleError],
  );

  const handleSubmit = useCallback(
    (values) => {
      const {
        campaign: { minimum_amount },
      } = order;
      const isInvalidAmount = minimum_amount
        ? parseFloat(values?.donation_order?.amount) <
          parseFloat(minimum_amount)
        : false;

      if (
        // Payment already ongoing
        (values?.transaction_token ||
          // No fund selected
          values?.donation_order?.gifts_attributes?.length < 1 ||
          // Amount is less than min amount
          isInvalidAmount ||
          // No amount selected
          !values?.donation_order?.gifts_attributes?.[0]?.amount) &&
        !canResubmit
      ) {
        return;
      }

      if (values?.pay_later) {
        submit();
      } else if (flagEvPaymentPageDonations) {
        submitBillingInfo(values);
      } else {
        setIsPaying(true);
      }
    },
    [order, canResubmit, flagEvPaymentPageDonations, submit, submitBillingInfo],
  );

  const submitPayLater = useCallback(() => {
    formikRef.current.setFieldValue('pay_later', true);
    formikRef.current.handleSubmit();
  }, []);

  const handlePaymentTransaction = (transactionToken) => {
    flushSync(() => {
      setIsPaymentDone(true);
      formikRef.current.setFieldValue('transaction_token', transactionToken);
    });

    submit();
  };

  const handleCancelPayment = useCallback(() => {
    setIsPaying(false);
  }, []);

  const paymentIsRecurring = () =>
    formikRef.current.values?.donation_order?.recurring_payment_enabled ===
    'true';

  return (
    <Formik initialValues={initialValues} onSubmit={handleSubmit}>
      {(formik) => {
        formikRef.current = formik;
        const gifts_attributes =
          formik.values?.donation_order?.gifts_attributes?.[0];
        const fundId = gifts_attributes?.fund_id;
        const paymentAccountExternalId =
          gifts_attributes?.payment_account_external_account_id;

        return (
          <FormikForm>
            <ErrorFocus />
            <Hidden name="authenticity_token" />
            <Hidden name="transaction_token" />
            <Hidden name="campaign_id" />
            <Hidden name="gateway" />
            <Hidden name="donation_order[external_id]" />
            <Hidden name="donation_order[promotion_code]" />
            <Hidden name="pay_later" />
            <AmountsAndFundsSections
              campaign={campaign}
              periodicities={periodicities}
              advancedOptionsEnabled={advanced_options_enabled}
              initialAmount={initialAmount}
              initialFundId={initialFundId}
            />
            <Box mb={5}>
              <Card noBorderRadiusOnSmallScreen>
                <YourInformationSection
                  order={order}
                  countries={countries}
                  advancedOptionsEnabled={advanced_options_enabled}
                />

                {!window.is_from_china &&
                  !currentUser &&
                  currentNetwork?.features?.donations?.config
                    ?.public_form_recaptcha_enabled && (
                    <FormikRecaptcha formik={formik} />
                  )}
                {isPaying ? (
                  <Payment
                    paymentGateway={payment_gateway}
                    campaign={campaign}
                    onHandlePaymentTransaction={handlePaymentTransaction}
                    onHandleSuccessPayment={handleSuccessPayment}
                    onHandleCancelPayment={handleCancelPayment}
                    fundId={fundId}
                    paymentAccountExternalId={paymentAccountExternalId}
                    email={formik.values?.donation_order?.email}
                    amount={formik.values?.donation_order?.amount}
                    paymentIsRecurring={paymentIsRecurring()}
                    currency={campaign.currency}
                    additionalData={formik.values}
                    url={donationBasePath(route)}
                  />
                ) : (
                  !isPaymentDone && (
                    <Box justifyContent="flex-end">
                      {payLaterEnabled && !paymentIsRecurring() ? (
                        <CommitPayLaterButton
                          disabled={submitDisabled}
                          onClick={submitPayLater}
                          payNowEnabled={payNowEnabled}
                        />
                      ) : null}
                      {payNowEnabled ? (
                        <Box ml={3}>
                          <SubmitButton
                            disabled={submitDisabled || isLoadingPaymentPage}
                            payLaterEnabled={payLaterEnabled}
                          />
                        </Box>
                      ) : null}
                    </Box>
                  )
                )}
                <ErrorsReminder />
              </Card>
            </Box>
          </FormikForm>
        );
      }}
    </Formik>
  );
};

Form.propTypes = {
  order: PropTypes.object.isRequired,
  initialAmount: PropTypes.string,
  initialFundId: PropTypes.string,
  initialMonthlyRecurring: PropTypes.bool,
  externalId: PropTypes.string,
  promotionCode: PropTypes.string,
  submitDisabled: PropTypes.bool,
  payLaterEnabled: PropTypes.bool,
  payNowEnabled: PropTypes.bool,
};

export default Form;
